/**
 * ``useProducerDetail`` — TanStack Query hook around
 * ``GET /api/query/producer/{id}/details``.
 *
 * Stream A PR 5.3. Feeds the Entity Details panel's CONFIGURATION
 * section (and downstream sections that reference cert binding /
 * onboarding provenance). The hook is enabled when a Platform
 * entity is selected; switching kind / clearing selection disables
 * the query.
 *
 * Cross-references:
 *   - REQ-UI-007 / REQ-UI-026 / REQ-UI-049.
 *   - REQ-AUTH-024..027.
 *   - Flow Q §"Frame 2" (CONFIGURATION section content schema).
 *   - ADR-024 §4 (Platform section schema).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type ProducerDetail = components["schemas"]["ProducerDetail"];

/** Roles permitted to read producer-detail per REQ-AUTH-024..027. */
export const PRODUCER_DETAIL_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Stale time matches the producers-list staleTime (30 s). */
export const PRODUCER_DETAIL_STALE_TIME_MS = 30_000;

export function producerDetailQueryKey(producerId: string): readonly unknown[] {
  return ["producer-detail", producerId] as const;
}

export interface UseProducerDetailOptions {
  /** Producer UUID. ``null`` disables the query. */
  producerId: string | null;
}

export function useProducerDetail(
  options: UseProducerDetailOptions,
): UseQueryResult<ProducerDetail, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(PRODUCER_DETAIL_READER_ROLES);
  const enabled = options.producerId !== null && hasRole;

  return useQuery<ProducerDetail, ApiError>({
    queryKey:
      options.producerId !== null
        ? producerDetailQueryKey(options.producerId)
        : ["producer-detail", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.producerId === null) {
        throw new ApiError(0, null, "useProducerDetail: query ran without producerId");
      }
      const url = `/api/query/producer/${encodeURIComponent(options.producerId)}/details`;
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isProducerDetail(raw)) {
        console.error(
          "[meridian/entity-details/use-producer-detail] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "producer-detail response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: PRODUCER_DETAIL_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isProducerDetail(value: unknown): value is ProducerDetail {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.producer_id === "string" &&
    typeof obj.display_name === "string" &&
    typeof obj.organization === "string" &&
    typeof obj.classification === "string" &&
    (obj.aggregator_id === null || typeof obj.aggregator_id === "string") &&
    typeof obj.created_at === "string" &&
    typeof obj.created_by === "string" &&
    typeof obj.schema_version === "number" &&
    typeof obj.metadata === "object" &&
    obj.metadata !== null
  );
}
